import React, { FC, useState, useRef, useEffect } from 'react';

import { Typography } from '@material-ui/core';

import { isEmpty } from 'lodash';

import {
  PlaceType,
  loadGoogleMapScript,
  DirectionType
} from '../../../core/GoogleMaps';

const directionService = { current: null };

interface GoogleDistanceCalculatorProps {
  origin?: PlaceType;
  destination?: PlaceType;
  onChange: (distance: number) => void;
}

const GoogleDistanceCalculator: FC<GoogleDistanceCalculatorProps> = ({
  origin,
  destination,
  onChange
}) => {
  const [distance, setDistance] = useState(-1);
  const loaded = useRef(false);

  if (!loaded.current) {
    loadGoogleMapScript();
    loaded.current = true;
  }

  useEffect(() => {
    let active = true;
    const google = (window as any).google;

    if (!directionService.current && google) {
      directionService.current = new google.maps.DirectionsService();
    }
    if (
      !directionService.current ||
      isEmpty(origin?.place_id) ||
      isEmpty(destination?.place_id)
    ) {
      setDistance(-1);
      onChange(-1);
      return undefined;
    }

    (directionService.current as any).route(
      {
        origin: { placeId: origin?.place_id },
        destination: { placeId: destination?.place_id },
        travelMode: 'DRIVING'
      },
      (response: DirectionType) => {
        if (
          active &&
          response &&
          response.routes &&
          response.routes.length > 0
        ) {
          const rawDistance = response.routes.reduce((s, r) => {
            return r.legs.reduce((ls, l) => l.distance.value + ls, s);
          }, 0);
          // m to miles and rounded up
          const roundedDistance = Math.round(rawDistance * 0.00062137);
          setDistance(roundedDistance);
          onChange(roundedDistance);
        } else {
          setDistance(-1);
          onChange(-1);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [origin?.place_id, destination?.place_id]); // eslint-disable-line react-hooks/exhaustive-deps

  let distanceText = '';
  if (origin) {
    if (distance >= 0) {
      if (distance < 1) {
        distanceText = ` less than 1 mile`;
      } else {
        distanceText = ` approximately ${distance} ${
          distance > 1 ? 'miles' : 'mile'
        }`;
      }
    } else {
      distanceText = 'not available';
    }
  }

  return (
    <Typography variant="h6" display="block">
      Delivery distance: {distanceText}
    </Typography>
  );
};

export default GoogleDistanceCalculator;
